/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import Slider from "react-slick";
import { createStyles } from "@material-ui/core/styles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

import { Grid } from "@material-ui/core";
import { Apps } from "@material-ui/icons";

// TC Kit
import Parallax from "components/Parallax/Parallax.jsx";
import Button from "components/CustomButtons/Button.jsx";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";

import { Modal } from "components/Modal/Modal";
import { getCourses, getImage, ApricotAction } from "client/actions/courses";
import { updateUser } from "client/actions/user";
import { selectorCourses } from "client/selectors/courseSelector";
import { courseType } from "client/reducers/courseReducer";

import { PhoenixWhy } from "client/views/LandingPage/Sections/SectionWhy";
import { PhoenixHow } from "client/views/LandingPage/Sections/SectionHow";
import { WelcomePage } from "client/views/Account/WelcomePage";
import { sLogo, sLandingCatch } from "assets/jss/views/ResStrings";
import { idExplore } from "assets/jss/views/ElementIDs";

import landingPageStyle, {
  mainImg,
} from "assets/jss/views/landingPage/landingPageStyle.jsx";
const styles = createStyles(landingPageStyle as any);

interface Props extends WithStyles<typeof styles> {
  getCourses: ApricotAction;
  getImage: ApricotAction;
  updateUser: ApricotAction;
  // store data
  user: any;
  courses: any[];
  courseImages: {};
  // component props
  history: any;
  match: any;
}

const Home: React.FC<Props> = ({
  getCourses,
  getImage,
  updateUser,
  user,
  courses,
  courseImages,
  history,
  match,
  classes,
}: Props) => {
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    getCourses();
    if (user && match.path === "/landing") {
      // no toast, no dispatch to store
      updateUser(user._id, { devw: window.innerWidth }, false, false);
    }
  }, []);

  useEffect(() => {
    if (courses) {
      courses.forEach(({ _id, caros, imageLink }) => {
        if (caros && imageLink && !courseImages[_id]) {
          getImage({ cid: _id, imgPath: imageLink });
        }
      });
    }
  }, [courses]);

  const courseButton = () => {
    const { crs } = user || {};
    return (
      <Button
        id={idExplore}
        round
        color="primary"
        size="lg"
        onClick={() => history.push(crs ? `/crp/${crs}` : "/cr_list")}>
        {crs ? (
          "Previous Course"
        ) : (
          <>
            <Apps />
            &nbsp;Explore Courses
          </>
        )}
      </Button>
    );
  };

  const renderCatch = () => {
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "flex-end",
          padding: "0 2rem",
        }}>
        <Grid container style={{ display: "flex", justifyContent: "center" }}>
          <Grid item sm={10} md={8}>
            <div className={classes.catch}>
              <h1>Apricad</h1>
              <h2>{sLogo}</h2>
              <h3 style={{ fontFamily: "Raleway" }}>{sLandingCatch}</h3>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  const renderAnnc = (course: any) => {
    const HtmlToReactParser = require("html-to-react").Parser;
    const parser = new HtmlToReactParser();

    return (
      <div style={{ padding: "1rem" }}>
        <div>{parser.parse(course.subtitle)}</div>
        <div>{parser.parse(course.category)}</div>
        {course.ytlist && (
          <div style={{ width: "100%", textAlign: "center" }}>
            <Button
              color="success"
              round
              onClick={() => setAnnouncement(course)}>
              {course.ytlist}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderItem = (course: any) => {
    const isCourse = course.ctype === courseType.COURSE;
    const isAnnc = course.ctype === courseType.ANNOUCEMENT;
    return courseImages[course._id] ? (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Card
          className={classes.carosItem}
          lectures={!isAnnc}
          background={!isAnnc}
          style={{
            cursor: isAnnc ? "default" : "pointer",
            backgroundImage: `url(data:image/jpeg;base64,${
              courseImages[course._id]
            })`,
            backgroundSize: "cover",
          }}
          onClick={() =>
            !isAnnc &&
            history.push(
              isCourse ? `/crp/${course._id}` : `/clsrm/${course.ytlist}`,
            )
          }>
          {course.ctype !== courseType.LINK && (
            <CardBody
              background={!isAnnc}
              style={{ maxWidth: "100%", padding: "0 0.5rem" }}>
              {isCourse ? (
                <>
                  <h6 style={{ margin: "1rem 0 2rem" }}>{course.category}</h6>
                  <h2>{course.title}</h2>
                </>
              ) : (
                renderAnnc(course)
              )}
            </CardBody>
          )}
        </Card>
      </div>
    ) : (
      // draw dummy carousel item on Server Side, so the initial dot number is consistent on server/client
      <div className={classes.carosItem} />
    );
  };

  const renderCourses = () =>
    courses.map((course: any, idx: number) => {
      return (
        course.caros && (
          <div key={idx}>
            <Grid
              container
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "2rem",
              }}>
              <Grid item sm={9} md={6}>
                {renderItem(course)}
              </Grid>
            </Grid>
          </div>
        )
      );
    });

  const renderMain = () => {
    const settings = {
      dots: true,
      infinite: true,
      speed: 1500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 3750,
    };

    return (
      <div>
        <Slider {...settings}>
          {renderCatch()}
          {renderCourses()}
        </Slider>
        <div style={{ width: "100%", textAlign: "center", marginTop: "-1rem" }}>
          {courseButton()}
        </div>
      </div>
    );
  };

  const renderBody = () => (
    <div className={classes.fullPageContent}>
      <div className={classes.container}>
        <PhoenixWhy />
        <PhoenixHow />
      </div>
    </div>
  );

  const renderAnncBody = (body: string) => {
    const HtmlToReactParser = require("html-to-react").Parser;
    const parser = new HtmlToReactParser();
    return parser.parse(body);
  };

  if (user && user.registered === false) {
    return (
      // If we use requireCheck() for landing page, in the case of just sign-up, the lag is so long and caused
      // ugly init screen (footer drawn on header, without app body). So we don't use requireCheck for LandingPage.
      <WelcomePage.component />
    );
  }

  if (!require("detect-node")) {
    window.scrollTo(0, 0);
  }
  return (
    <>
      {announcement !== null && (
        <Modal title={announcement.title} onExit={() => setAnnouncement(null)}>
          {renderAnncBody(announcement.subcategory)}
        </Modal>
      )}
      <Parallax image={mainImg} filter="dark">
        <div className={classes.container}>
          {courses && <>{renderMain()}</>}
        </div>
      </Parallax>
      {renderBody()}
    </>
  );
};

function loadData(store: any) {
  return store.dispatch(getCourses(""));
}

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    courses: selectorCourses(state),
    courseImages: state.state_course.courseImages,
  };
}

export default {
  loadData,
  component: withStyles(styles)(
    connect(mapStateToProps, { getCourses, getImage, updateUser })(Home),
  ),
};

export const HomeTest = withStyles(styles)(Home);
